import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { nilAthleteFoundationManagementContent } from '../content/educational/nilAthleteFoundationManagementContent';
import { fiscalSponsorshipNILAthletesContent } from '../content/fiscalSponsorNILAthletesContent';

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 32;
    const footerHeight = 12;
    const lineSpacing = 4;
    const paragraphSpacing = 2;
    const bulletIndent = 12;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Helper function to process markdown text
    const processMarkdown = (text) => {
      let isBold = false;
      let processedText = text;
      
      // Handle bold text
      processedText = processedText.replace(/\*\*(.*?)\*\*/g, (match, content) => {
        isBold = true;
        doc.setFont('helvetica', 'bold');
        return content;
      });

      // Handle italic text
      processedText = processedText.replace(/\*(.*?)\*/g, (match, content) => {
        if (!isBold) {
          doc.setFont('helvetica', 'italic');
        }
        return content;
      });

      // Reset font if no formatting was applied
      if (!isBold) {
        doc.setFont('helvetica', 'normal');
      }

      return { text: processedText, isBold };
    };

    // Helper function to handle bullet points and numbered lists
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const bulletIndent = 12;
      const numberWidth = 8;
      
      lines.forEach(line => {
        if (line.trim().startsWith('- ')) {
          // Bullet point with increased indentation
          const bulletText = line.substring(2);
          doc.setFont('helvetica', 'normal');
          doc.text('•', margin + bulletIndent, currentY);
          const { text: processedText } = processMarkdown(bulletText);
          const bulletLines = doc.splitTextToSize(processedText, contentWidth - (bulletIndent + 8));
          
          bulletLines.forEach((bLine, index) => {
            if (currentY + 5 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
            }
            doc.text(bLine, margin + bulletIndent + 6, currentY);
            currentY += 5;
          });
          currentY += 1;
        } else if (line.match(/^\d+\)/)) {
          // Numbered list with proper indentation and formatting
          const numberPart = line.substring(0, line.indexOf(')') + 1);
          const textPart = line.substring(line.indexOf(')') + 1).trim();
          const { text: processedText } = processMarkdown(textPart);
          
          // Split the processed text into label and description
          let label = '';
          let description = processedText;
          
          if (processedText.includes(':')) {
            [label, description] = processedText.split(':').map(s => s.trim());
            
            // Set font for the label part
            if (processedText.includes('**')) {
              doc.setFont('helvetica', 'bold');
            }
          }

          const numberLines = doc.splitTextToSize(description, contentWidth - (bulletIndent + numberWidth + 8));
          
          numberLines.forEach((nLine, index) => {
            if (currentY + 5 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
            }
            
            if (index === 0) {
              // Place the number
              doc.text(numberPart, margin + bulletIndent, currentY);
              
              // Place the bold label if it exists
              if (label) {
                doc.setFont('helvetica', 'bold');
                doc.text(label + ':', margin + bulletIndent + numberWidth + 2, currentY);
                const labelWidth = doc.getTextWidth(label + ': ');
                
                // Reset font and place description
                doc.setFont('helvetica', 'normal');
                doc.text(nLine, margin + bulletIndent + numberWidth + 2 + labelWidth, currentY);
              } else {
                // Just place the text if no label
                doc.text(nLine, margin + bulletIndent + numberWidth + 2, currentY);
              }
            } else {
              // Align wrapped lines
              doc.setFont('helvetica', 'normal');
              doc.text(nLine, margin + bulletIndent + numberWidth + 2, currentY);
            }
            currentY += 5;
          });
          
          // Reset font
          doc.setFont('helvetica', 'normal');
          currentY += 1;
        } else {
          // Regular text remains at normal margin
          const { text: processedText } = processMarkdown(line);
          const textLines = doc.splitTextToSize(processedText, contentWidth);
          
          textLines.forEach(tLine => {
            if (currentY + 5 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
            }
            doc.text(tLine, margin, currentY);
            currentY += 5;
          });
          currentY += 1;
        }
      });
      
      return currentY;
    };

    // Your existing header, footer, and CTA functions remain the same
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      // Gradient title effect
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Starting a Nonprofit Cause never got easier!', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    };

    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 30;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 5, 5, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to Get Started?', pageWidth / 2, yPosition + 20, { align: 'center' });

      doc.setFontSize(12);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 30, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 10, yPosition + 7.5, qrSize, qrSize);
    };

    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      // Add title with optimized styling
      doc.setFontSize(20);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 6, { align: 'center' });
      yPosition += (titleLines.length * 7) + 12;

      content.forEach((section, index) => {
        if (yPosition + 35 > pageHeight - footerHeight) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(5, 150, 105);
        doc.text(section.heading, margin, yPosition);
        yPosition += 8;

        // Process section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(31, 41, 55);
        
        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          yPosition = processList(paragraph, yPosition);
          yPosition += paragraphSpacing;
        });

        if (index === content.length - 1) {
          if (yPosition + 40 > pageHeight - footerHeight) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }
          addCallToAction(yPosition);
        }
      });
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateNILFoundationOverview = async () => {
  return generatePDF(
    nilAthleteFoundationManagementContent.sections,
    nilAthleteFoundationManagementContent.title,
    nilAthleteFoundationManagementContent.keywords
  );
};

export const generateNILFiscalSponsorshipGuide = async () => {
  return generatePDF(
    fiscalSponsorshipNILAthletesContent.sections,
    fiscalSponsorshipNILAthletesContent.title,
    fiscalSponsorshipNILAthletesContent.keywords
  );
}; 